import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CandlestickSeries,
  ColorType,
  LineStyle,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type AutoscaleInfoProvider,
  type UTCTimestamp,
} from "lightweight-charts";
import { fetchKlines, subscribeKline } from "../lib/binance";
import { roundToTick } from "../lib/calculator";
import type { Candle, SavedPlan } from "../types";

interface Props {
  plan: SavedPlan;
  onStopChange: (price: number) => void;
  onDraggingChange: (dragging: boolean) => void;
}

const INTERVALS = [
  { value: "1m", label: "1分" },
  { value: "5m", label: "5分" },
  { value: "15m", label: "15分" },
  { value: "1h", label: "1时" },
  { value: "4h", label: "4时" },
];

function syntheticCandles(entry: number): Candle[] {
  let previous = entry * 0.985;
  return Array.from({ length: 120 }, (_, index) => {
    const wave = Math.sin(index / 8) * entry * 0.008;
    const drift = ((index - 60) / 60) * entry * 0.006;
    const close = entry * 0.99 + wave + drift;
    const open = previous;
    previous = close;
    return {
      time: Math.floor(Date.now() / 1000) - (119 - index) * 900,
      open,
      close,
      high: Math.max(open, close) + entry * 0.0025,
      low: Math.min(open, close) - entry * 0.0025,
    };
  });
}

export default function KlineChart({ plan, onStopChange, onDraggingChange }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const planRef = useRef(plan);
  const dragOriginRef = useRef(plan.inputs.stopPrice);
  const [interval, setIntervalValue] = useState("15m");
  const [loading, setLoading] = useState(true);
  const [streamLive, setStreamLive] = useState(false);
  const [chartMessage, setChartMessage] = useState("");
  const [stopCoordinate, setStopCoordinate] = useState(0);
  const [entryCoordinate, setEntryCoordinate] = useState(0);
  const [takeProfitCoordinate, setTakeProfitCoordinate] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  planRef.current = plan;

  const priceFormat = useMemo(() => ({
    type: "price" as const,
    precision: plan.result.pricePrecision,
    minMove: plan.rules.priceTick,
  }), [plan.result.pricePrecision, plan.rules.priceTick]);

  const autoscaleInfoProvider = useCallback<AutoscaleInfoProvider>((baseImplementation) => {
    const base = baseImplementation();
    if (!base) return null;
    const latestPlan = planRef.current;
    const prices = [latestPlan.inputs.entryPrice, latestPlan.inputs.stopPrice];
    if (latestPlan.inputs.takeProfitLinked !== false) prices.push(latestPlan.result.takeProfitPrice);
    const minValue = Math.min(base.priceRange?.minValue ?? prices[0], ...prices);
    const maxValue = Math.max(base.priceRange?.maxValue ?? prices[0], ...prices);
    const padding = Math.max((maxValue - minValue) * 0.06, latestPlan.rules.priceTick * 4);
    return {
      ...base,
      priceRange: { minValue: Math.max(0, minValue - padding), maxValue: maxValue + padding },
    };
  }, []);

  const refreshCoordinates = () => {
    const series = seriesRef.current;
    if (!series) return;
    const latestPlan = planRef.current;
    setStopCoordinate(series.priceToCoordinate(latestPlan.inputs.stopPrice) ?? 0);
    setEntryCoordinate(series.priceToCoordinate(latestPlan.inputs.entryPrice) ?? 0);
    setTakeProfitCoordinate(series.priceToCoordinate(latestPlan.result.takeProfitPrice) ?? 0);
  };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const chart = createChart(host, {
      width: host.clientWidth,
      height: host.clientHeight || 360,
      layout: {
        background: { type: ColorType.Solid, color: "#ffffff" },
        textColor: "#848e9c",
        attributionLogo: true,
      },
      grid: {
        vertLines: { color: "#f1f2f4" },
        horzLines: { color: "#f1f2f4" },
      },
      rightPriceScale: { borderColor: "#eaecef", scaleMargins: { top: 0.12, bottom: 0.12 } },
      timeScale: { borderColor: "#eaecef", timeVisible: true, secondsVisible: false },
      crosshair: {
        vertLine: { color: "#b7bdc6", labelBackgroundColor: "#707a8a" },
        horzLine: { color: "#b7bdc6", labelBackgroundColor: "#707a8a" },
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: "#0ecb81",
      downColor: "#f6465d",
      borderVisible: false,
      wickUpColor: "#0ecb81",
      wickDownColor: "#f6465d",
      priceFormat,
      autoscaleInfoProvider,
    });
    chartRef.current = chart;
    seriesRef.current = series;

    let resizeFrame = 0;
    const resizeObserver = new ResizeObserver(() => {
      chart.applyOptions({ width: host.clientWidth, height: host.clientHeight || 360 });
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(refreshCoordinates);
    });
    resizeObserver.observe(host);
    chart.timeScale().subscribeVisibleLogicalRangeChange(refreshCoordinates);

    return () => {
      resizeObserver.disconnect();
      window.cancelAnimationFrame(resizeFrame);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(refreshCoordinates);
      // Lightweight Charts 可能仍有一帧内部绘制排队；稍后销毁可避免切页时访问已释放画布。
      window.setTimeout(() => chart.remove(), 80);
      chartRef.current = null;
      seriesRef.current = null;
    };
    // 图表实例只在交易对变化时重建，拖线数值变化不应重置缩放和可视范围。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.inputs.symbol]);

  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return;
    series.applyOptions({ priceFormat, autoscaleInfoProvider });
    const lines = [
      series.createPriceLine({
        price: plan.inputs.entryPrice,
        color: "#f0b90b",
        lineWidth: 2,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: "开仓",
      }),
      series.createPriceLine({
        price: plan.inputs.stopPrice,
        color: "#f6465d",
        lineWidth: dragging ? 3 : 2,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: "止损",
      }),
    ];
    if (plan.inputs.takeProfitLinked !== false) {
      lines.push(series.createPriceLine({
        price: plan.result.takeProfitPrice,
        color: "#0ecb81",
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: "止盈",
      }));
    }
    if (plan.result.approximateLiquidationPrice) {
      lines.push(series.createPriceLine({
        price: plan.result.approximateLiquidationPrice,
        color: "rgba(240,185,11,0.85)",
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        axisLabelVisible: false,
        title: "粗略强平",
      }));
    }
    requestAnimationFrame(refreshCoordinates);
    return () => lines.forEach((line) => series.removePriceLine(line));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.inputs.entryPrice, plan.inputs.stopPrice, plan.result.takeProfitPrice, plan.result.approximateLiquidationPrice, dragging, priceFormat, autoscaleInfoProvider]);

  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return;
    let cancelled = false;
    let unsubscribe = () => {};
    setLoading(true);
    setStreamLive(false);
    setChartMessage("");

    const load = async () => {
      try {
        const candles = plan.source === "binance"
          ? await fetchKlines(plan.inputs.symbol, interval, 300)
          : syntheticCandles(plan.inputs.entryPrice);
        if (cancelled) return;
        series.setData(candles.map((candle) => ({
          ...candle,
          time: candle.time as UTCTimestamp,
        })));
        chart.timeScale().fitContent();
        setChartMessage(plan.source === "manual" ? "手动价格模式：K 线为示意数据" : "");
        if (plan.source === "binance") {
          unsubscribe = subscribeKline(plan.inputs.symbol, interval, (candle) => {
            series.update({ ...candle, time: candle.time as UTCTimestamp });
            requestAnimationFrame(refreshCoordinates);
          }, setStreamLive);
        }
      } catch (error) {
        if (cancelled) return;
        const candles = syntheticCandles(plan.inputs.entryPrice);
        series.setData(candles.map((candle) => ({ ...candle, time: candle.time as UTCTimestamp })));
        chart.timeScale().fitContent();
        setChartMessage(`${error instanceof Error ? error.message : "K 线加载失败"}，当前显示示意 K 线`);
      } finally {
        if (!cancelled) {
          setLoading(false);
          requestAnimationFrame(refreshCoordinates);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.inputs.symbol, plan.source, interval]);

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    dragOriginRef.current = plan.inputs.stopPrice;
    setDragging(true);
    onDraggingChange(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragging || !seriesRef.current || !hostRef.current) return;
    const rect = hostRef.current.getBoundingClientRect();
    const rawPrice = seriesRef.current.coordinateToPrice(event.clientY - rect.top);
    if (rawPrice == null || !Number.isFinite(rawPrice)) return;
    const minimumDistance = plan.rules.priceTick;
    const bounded = plan.inputs.direction === "long"
      ? Math.min(rawPrice, plan.inputs.entryPrice - minimumDistance)
      : Math.max(rawPrice, plan.inputs.entryPrice + minimumDistance);
    onStopChange(roundToTick(Math.max(minimumDistance, bounded), plan.rules.priceTick));
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragging) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    setDragging(false);
    setCanUndo(Math.abs(dragOriginRef.current - plan.inputs.stopPrice) >= plan.rules.priceTick);
    onDraggingChange(false);
  };

  const riskTop = Math.min(stopCoordinate, entryCoordinate);
  const riskHeight = Math.abs(stopCoordinate - entryCoordinate);
  const rewardTop = Math.min(takeProfitCoordinate, entryCoordinate);
  const rewardHeight = Math.abs(takeProfitCoordinate - entryCoordinate);

  return (
    <section className="chart-card" aria-label="可拖动止损 K 线">
      <div className="chart-toolbar">
        <div className="interval-switch" aria-label="K线周期">
          {INTERVALS.map((item) => (
            <button
              type="button"
              key={item.value}
              className={interval === item.value ? "active" : ""}
              onClick={() => setIntervalValue(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="chart-toolbar-actions">
          <span className={streamLive ? "live-dot is-live" : "live-dot"} />
          <small>{streamLive ? "实时" : loading ? "加载中" : "快照"}</small>
          {canUndo && (
            <button type="button" className="undo-stop" onClick={() => { onStopChange(dragOriginRef.current); setCanUndo(false); }}>
              撤销
            </button>
          )}
        </div>
      </div>
      <p className="chart-instruction">上下拖动止损线，开仓参数实时反推</p>
      <div className="chart-stage">
        <div ref={hostRef} className="chart-host" />
        {entryCoordinate > 0 && riskHeight > 0 && (
          <div className="risk-zone" style={{ top: riskTop, height: riskHeight }} />
        )}
        {plan.inputs.takeProfitLinked !== false && entryCoordinate > 0 && rewardHeight > 0 && (
          <div className="reward-zone" style={{ top: rewardTop, height: rewardHeight }} />
        )}
        {stopCoordinate > 0 && (
          <button
            type="button"
            className={`stop-handle ${dragging ? "is-dragging" : ""}`}
            style={{ top: stopCoordinate }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            aria-label="拖动止损线"
          >
            <span>⇅</span>
            <b>拖动止损</b>
          </button>
        )}
      </div>
      {chartMessage && <p className="chart-message">{chartMessage}</p>}
      <p className="chart-attribution">
        K 线组件由 <a href="https://www.tradingview.com/" target="_blank" rel="noreferrer">TradingView Lightweight Charts™</a> 提供
      </p>
    </section>
  );
}
